import React, { useMemo } from "react";
import { css, cx } from "@linaria/core";
import { styled } from "@linaria/react";
import {
  FullViewportButtonMarker,
  FullViewportButtonMarkerActive,
  FullViewportButtonMarkerInactive,
} from "@ui/FullViewportButton";
import { colors, spacings } from "@ui/Theme";
import ChartCard from "@ui/Highcharts/ui/Card";
import HighchartsComponent from "@ui/Highcharts";
import type { Project } from "../../../../../../../../../api/useProjects";
import type { Source } from "../../../../../../../../../api/useSources";
import { useQuantityIndex, type Quantity } from "../../../../../../../../../api/useQuantities";
import type { Failure, Setup } from "../../../../../../../../../api/useSetups";
import type { ActiveAnalysis } from "../../../../../../../../../api/useActiveAnalysesStream";
import type { IndexedFailureMetadata } from "../../../../../../../../../api/useFailureMetadata";
import type { IndexedFailureValues } from "../../../../../../../../../api/useIndexedFailureValues";
import type { IndexedManualFailureValues } from "../../../../../../../../../api/useManualFailureValues";
import { exportWidgetTarget, HIDE_ON_WIDGET_EXPORT } from "@ui/ExportWidgetButton";
import DiagnosisLegend from "../../../../../../../Chart/Legend/DiagnosisLegend";
import CommonLegend from "../../../../../../../Chart/Legend/CommonLegend";
import { useDiagnosticWithQuantityNoDataIntervals } from "../../../../../../../useNoDataIntervals";
import { Divider } from "@ui/Highcharts/ui/Tooltip";
import { EXCLUDE_FULLSCREEN } from ".";
import type { ManualFailure } from "../../../../../../../../../api/useManualFailures";
import { useCanUserDoOnProject } from "../../../../../../../../canUserDo";
import { usePublicationFlagDisplay } from "../../../../../../../Analysis/TogglePublicationDisplay";
import { useSettings } from "../../../../../../../../../api/useSettings";
import useVisibleSeries from "@ui/Highcharts/hooks/useVisibleSeries";
import { Label } from "@ui/Highcharts/ui/Legend";
import FaultEventLegend from "../../../../../../../Chart/Legend/FaultEventLegend";
import BatchFaultStatusChange from "./BatchFaultStatusChange";
import { useEventPeriod } from "../../../../../../hooks/useEventPeriod";
import useSelectedAnalyses from "../../../../../../../Analysis/useSelectedAnalyses";
import { useDisplayAllFaults } from "../useDisplayAllFaults";
import { FAULT_AMPLITUDE_CHART, useFaultGraphOptions } from "./hook/useFaultGraphOptions";
import type { Event } from "../../../../../../../../../api/useEvents";
import { getAnalysisSetup } from "diagnostics-domain/setup/getSetup";
import { getEventFaultNames } from "../../../../../../../../../domain/event";
import { CLASSIC_FAILURE, MANUAL_FAILURE } from "../../../../../../../Chart/series/serieIds";
import classNames from "classnames";
import Modal from "@ui/Modal";
import { PencilLine, RepeatLine } from "@ui/IconV2/react";
import Button from "@ui/Button";
import { getEnhancedFaultStatusText } from "../../../../../../../Fault";
import Text from "@ui/Text";
import t from "../../../../../../../../../i18n";
import ModalFormDetectionsStatusChange from "../../../../../../components/TransactionalModals/ModalFormDetectionsStatusChange";
import useSuspectedFailuresInExplanation from "../../../../../../../Diagnosis/useSuspectedFailuresInExplanation";
import { useModals } from "@ui/lib/hooks/useModal";
import useSeries from "./hook/useSeries";
import { filterSeriesByFocus } from "../isFocusedFaultSeries";
import useOptions from "./hook/useOptions";
import { getOtherSerieDescription } from "../../../../../../../Diagnosis/FailureEvolution";
import { getEventVisibleSeries } from "../../../../../../utils/event";
import ManualFaultImpactTimeWindowsChange from "../../../../ManualFaultEvent/components/ManualFaultImpactTimeWindowsChange";

const FAULT_DETECTIONS_STATUS_CHANGE_MODAL = "fault-detections-status-change" as const;

const ChartChildrenWrapper = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: min-content 1fr;
  grid-template-rows: 400px min-content min-content min-content min-content;
  width: 100%;

  & > *:nth-child(1) {
    /* Chart legend (vertical) */
    grid-area: 1 / 1 / 2 / 2;
  }
  & > *:nth-child(2) {
    /* Chart */
    grid-area: 1 / 2 / 2 / 3;
  }
  & > *:nth-child(3) {
    /* legend */
    padding-top: 24px;
    grid-area: 2 / 1 / 3 / 3;
    width: 100%;

    display: flex;
    gap: ${spacings.m};
    justify-content: space-between;

    & > div {
      flex: 1;
      :not(:empty) {
        display: flex;
        column-gap: 16px;
        flex-wrap: wrap;
        row-gap: 8px;
        justify-content: flex-end;
        min-height: 18px;
      }
    }
  }
  & > *:nth-child(4) {
    /* Divider */
    margin-top: 24px;
    grid-area: 3 / 2 / 4 / 3;
  }
  & > *:nth-child(5) {
    /* Batch Change Status */
    padding-top: 24px;
    grid-area: 4 / 2 / 5 / 3;
  }
  & > *:nth-child(6) {
    /* Status applied new detections */
    padding-top: ${spacings.m};
    grid-area: 5 / 2 / 6 / 3;
  }

  .${FullViewportButtonMarkerActive} &,
  .${FullViewportButtonMarkerInactive} & {
    height: 100%;
  }

  .${FullViewportButtonMarkerActive} & {
    grid-template-rows: 1fr min-content;
  }

  .${FullViewportButtonMarkerActive} & .${EXCLUDE_FULLSCREEN} {
    display: none;
  }

  .${FullViewportButtonMarkerInactive} & .${EXCLUDE_FULLSCREEN} {
    display: block;
  }
`;

interface P {
  title: React.JSX.Element;
  subtitle: React.JSX.Element;
  legendLeft?: React.ReactNode;

  eventScope: string;
  event: Event;
  project: Project;
  quantities: Quantity[];
  activeAnalyses: ActiveAnalysis[];
  setups: Setup[];
  source: Source;
  indexedFailureMetadata: IndexedFailureMetadata;
  indexedFailureValues: IndexedFailureValues;
  indexedManualFailureValues: IndexedManualFailureValues;
}

const DiagnosisHighcharts = ({
  title,
  subtitle,
  legendLeft,

  eventScope,
  event,
  project,
  quantities,
  setups,
  source,
  activeAnalyses,
  indexedFailureMetadata,
  indexedFailureValues,
  indexedManualFailureValues,
}: P) => {
  const settings = useSettings();
  const { modal, openModal, closeModal } = useModals<string>();

  const chartRef = React.useRef<Highcharts.Chart>(null);
  const chartRefs = React.useMemo(() => [chartRef], [chartRef]);
  const canUserWriteProject = useCanUserDoOnProject("PROJECT_WRITE", project.id);
  const [publicationFlagDisplayed] = usePublicationFlagDisplay({
    scope: project.id,
    defaultValue: settings.DISPLAY_PUBLICATION_MARKERS,
  });

  const [displayAllFaults, setDisplayAllFaults] = useDisplayAllFaults({
    scope: eventScope,
  });

  const { graphOptionMenu, graphOptionSelector } = useFaultGraphOptions({
    event,
    quantities,
  });

  const [suspectedFailuresIncluded] = useSuspectedFailuresInExplanation({ scope: project.id });
  const { selectedAnalyses } = useSelectedAnalyses(project.id, activeAnalyses);
  const analysis = selectedAnalyses[0];
  const analysisSetup = analysis ? getAnalysisSetup(setups, analysis) : null;

  const [period, _, __, ___, ____, defaultFaultEventPeriod] = useEventPeriod({
    event,
    source,
    project,
    analyses: activeAnalyses,
    boundaries: "active-analyses",
  });

  const quantityIndex = useQuantityIndex(project.id);

  const qoi = React.useMemo(
    () =>
      graphOptionMenu.graphType === FAULT_AMPLITUDE_CHART
        ? null
        : quantityIndex[graphOptionSelector.impactSelector] || null,
    [graphOptionMenu, graphOptionSelector, quantityIndex],
  );
  const yAxisId = `project-id-${project.id}-${
    qoi === null ? FAULT_AMPLITUDE_CHART : `qoi-id-${qoi.id}`
  }`;

  const series = useSeries({
    project,
    yAxisId,
    setups,
    analyses: activeAnalyses,
    indexedFailureMetadata,
    indexedFailureValues,
    indexedManualFailureValues,
    qoi,
    includeSuspectedFailure: suspectedFailuresIncluded,
    quantityIndex,
    actionAllowed: false,
    publicationFlagDisplayed,
    failureSerieType:
      graphOptionMenu.graphType === FAULT_AMPLITUDE_CHART ? "AMPLITUDE" : "OBSERVABLE",
  });
  const failureNames = getEventFaultNames(event);
  const eventSeries = useMemo<typeof series>(
    () =>
      series.flatMap((serie): typeof series => {
        if (event.type === "MANUAL") {
          return serie.id?.startsWith(MANUAL_FAILURE) &&
            failureNames.some(failureName => serie.id?.endsWith(failureName))
            ? [serie]
            : [];
        }

        const failureName = failureNames.find(
          name => serie.id?.startsWith(CLASSIC_FAILURE) && serie.id.endsWith(name),
        );
        return failureName === undefined ? [] : [serie];
      }),
    [event.type, failureNames, series],
  );

  /** depending on the filters on “displayAllFaults” a subset of failures may be displayed */
  const seriesToShow = React.useMemo(() => {
    if (graphOptionMenu.graphType === FAULT_AMPLITUDE_CHART) {
      return eventSeries;
    }

    return filterSeriesByFocus({
      series,
      event,
      displayAllFaults,
      failureNames,
      getEventVisibleSeries,
    });
  }, [graphOptionMenu, series, displayAllFaults, failureNames, eventSeries, event]);
  const [visibleSeries, onSerieClick] = useVisibleSeries(
    `${eventScope}-${qoi === null ? "amplitude" : `qoi-${displayAllFaults}`}`,
    seriesToShow,
  );

  const chartOptionMenu = useMemo(
    () => ({
      label: String(
        graphOptionMenu.graphTypeOptions.find(o => o.value === graphOptionMenu.graphType)?.label,
      ),
      value: graphOptionMenu.graphType,
    }),
    [graphOptionMenu.graphType, graphOptionMenu.graphTypeOptions],
  );

  const options = useOptions({
    source,
    project,
    failureNames,
    chartRef,
    chartTypeOption: chartOptionMenu,
    yAxisId,
    analyses: activeAnalyses,
    indexedFailureValues,
    qoi,
    series: visibleSeries,
    event,
  });
  const renderOtherSeriesTooltip = React.useCallback(
    (serie: Highcharts.SeriesOptions) => {
      const serieId = serie.id;

      return (
        <>
          <Divider />
          {getOtherSerieDescription({
            serieId,
            chartType: graphOptionSelector.impactSelector,
            suspectedFailuresIncluded,
          })}
        </>
      );
    },
    [graphOptionSelector, suspectedFailuresIncluded],
  );
  const renderLabel = React.useCallback(
    (
      _failure: Failure | ManualFailure | undefined,
      serie:
        | Highcharts.SeriesLineOptions
        | Highcharts.SeriesArearangeOptions
        | Highcharts.SeriesAreaOptions
        | Highcharts.SeriesScatterOptions,
    ) => {
      const failureDisplayName =
        typeof serie.custom === "object" &&
        serie.custom !== null &&
        "failureDisplayName" in serie.custom
          ? serie.custom["failureDisplayName"]
          : serie.name;

      return <Label>{failureDisplayName}</Label>;
    },
    [],
  );
  const noDataIntervals = useDiagnosticWithQuantityNoDataIntervals(
    activeAnalyses,
    indexedFailureValues,
  );

  const legend = React.useMemo(
    () => (
      <div>
        <DiagnosisLegend chartRef={chartRef} series={visibleSeries} period={period} />
        <CommonLegend
          chartRef={chartRef}
          analyses={activeAnalyses}
          series={visibleSeries}
          period={period}
          noDataIntervals={noDataIntervals}
          defaultFaultEventPeriod={defaultFaultEventPeriod}
          chartType="diagnosis"
          projectId={project.id}
        />
      </div>
    ),
    [visibleSeries, period, activeAnalyses, noDataIntervals, defaultFaultEventPeriod, project.id],
  );

  const statusAppliedNewDetections = React.useMemo(
    () =>
      event.type === "DIAGNOSED" ? (
        <div>
          <div className={appliedNewDetectionStatus}>
            <span>
              <RepeatLine color={colors.carbon800} />
              <Text as="span">
                {t(
                  "project.fault_event.evolution_chart.batch_status_change.status_applied_new_detections",
                )}
              </Text>
            </span>
            <span>
              <Text type="body1SemiBold" as="span">
                {getEnhancedFaultStatusText(event.metadata.newDiagnosesStatus)}
              </Text>
              {event.metadata.status !== "CLOSED" && canUserWriteProject && (
                <Button
                  variant="link"
                  onClick={() => openModal(FAULT_DETECTIONS_STATUS_CHANGE_MODAL)}
                  leftIcon={<PencilLine />}
                >
                  {t("commons.message_card.edit")}
                </Button>
              )}
            </span>
          </div>

          <Modal
            title={t("project.fault_event.evolution_chart.detections_status_change.modal.title")}
            isOpen={modal === FAULT_DETECTIONS_STATUS_CHANGE_MODAL}
            onRequestClose={closeModal}
          >
            <ModalFormDetectionsStatusChange
              projectId={project.id}
              event={event}
              closeModal={closeModal}
            />
          </Modal>
        </div>
      ) : null,
    [project.id, event, modal, openModal, closeModal, canUserWriteProject],
  );

  return (
    <ChartCard
      title={title}
      subtitle={subtitle}
      legendLeft={legendLeft}
      className={cx(FailureEvolutionChartCard, FullViewportButtonMarker, exportWidgetTarget)}
    >
      <ChartChildrenWrapper>
        <FaultEventLegend
          project={project}
          chartRefs={chartRefs}
          series={visibleSeries}
          period={period}
          onLegendItemClick={onSerieClick}
          setup={analysisSetup}
          renderOtherSeriesTooltip={renderOtherSeriesTooltip}
          renderLabel={renderLabel}
          chartType={graphOptionMenu.graphType}
          displayAllFaults={displayAllFaults}
          onChangeDisplayAllFaults={() => setDisplayAllFaults(v => !v)}
        />
        <HighchartsComponent
          name="Diagnosis overview"
          ref={chartRef}
          options={options}
          selectablePoints
        />
        <div>{legend}</div>
        {event.type === "DIAGNOSED" || event.type === "MANUAL" ? (
          <>
            <Divider
              className={classNames(EXCLUDE_FULLSCREEN, HIDE_ON_WIDGET_EXPORT, styledDivider)}
            />
            {event.type === "DIAGNOSED" && (
              <>
                <BatchFaultStatusChange
                  className={classNames(EXCLUDE_FULLSCREEN, HIDE_ON_WIDGET_EXPORT)}
                  project={project}
                  event={event}
                  period={period}
                />
                {statusAppliedNewDetections}
              </>
            )}
            {event.type === "MANUAL" && (
              <ManualFaultImpactTimeWindowsChange
                project={project}
                manualFaultEvent={event}
                quantities={quantities}
              />
            )}
          </>
        ) : null}
      </ChartChildrenWrapper>
    </ChartCard>
  );
};

export default DiagnosisHighcharts;

// #region CSS

const appliedNewDetectionStatus = css`
  display: inline-flex;
  align-items: center;
  gap: ${spacings.xxs};
  flex-wrap: wrap;

  & > span {
    /* Icon + Title */
    display: inline-flex;
    flex-wrap: nowrap;
    align-items: center;
    gap: ${spacings.xxs};
  }
`;
const styledDivider = css`
  margin-bottom: 0;
  margin-top: 0;
`;
const FailureEvolutionChartCard = css`
  :not(.${FullViewportButtonMarker}) {
    max-height: 80vh;
  }
`;
// #endregion CSS
